import { IconButton, Switch, TableRow } from "@mui/material";
import { FC, useCallback, useState } from "react";
import StyledTableCell from "../../common/StyledTableCell";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLazyQuery } from "@apollo/react-hooks";
import { GetUrlDocument } from "../../../generated";
import useSnackBars from "../../../hooks/useSnackbar";
import DownloadFile from "../../../lib/utils/DownloadFile";

type Link = {
  key: string;
  text: string;
  url: string;
};

interface Row {
  question_id: string;
  question: string;
  answer_id: string;
  answers: string[];
  template: string;
  questionnaire_answer: string;
	links: [Link];
}

interface FTRQuestionnaireRowProps {
  index: number;
  row: Row;
  answer: string; // Current answer for this question
  onAnswerChange: (questionId: string, answer: string) => void; // Function to handle answer changes
}

/**
 * Display FTR Row Question
 * @param index index value
 * @param row row data
 * @param answer selected answer yes or no
 * @param onAnswerChange handle to be called when answer switch is toggled
 * @returns 
 */
const FTRQuestionnaireRow: FC<FTRQuestionnaireRowProps> = ({
  index,
  row,
  answer,
  onAnswerChange,
}: FTRQuestionnaireRowProps) => {
  const { setAlerts } = useSnackBars();
  const [disabledIcon, setDisabledIcon] = useState(false);
  const { question_id, question, template, links } = row;

  const [getRoleTemplateURL] = useLazyQuery(GetUrlDocument, {
    onCompleted: (data) => {
      DownloadFile(data?.getTemplateS3URL, template);
      setDisabledIcon(false);
    },
    onError: () => {
      setDisabledIcon(false);
      setAlerts([
        {
          severity: "error",
          msg: "Failed to fetch the template. Please try again.",
        },
      ]);
    },
  });

  const handleDownloadClick = useCallback(() => {
    const basePath = "process-documents/";
    setDisabledIcon(true);

    getRoleTemplateURL({
      variables: { template: `${basePath}${template}` },
    });

    // Toggle to "yes" if not already, only on download
    if (answer !== "yes") {
      onAnswerChange(question_id, "yes");
    }
  }, [getRoleTemplateURL, template, question_id, answer, onAnswerChange]);

  const handleSwitchToggle = useCallback(() => {
    const newAnswer = answer === "yes" ? "no" : "yes";
    onAnswerChange(question_id, newAnswer);
  }, [answer, question_id, onAnswerChange]);

	const renderQuestionWithLinks = (
		question: string,
		links: Link[]
	): JSX.Element => {
		// Replace placeholders with JSX using a regex
		const parts = question.split(/(\[\{.+?\}\])/g); // matches [{key}]

		return (
			<>
				{parts.map((part, idx) => {
					const match = part.match(/\[\{(.+?)\}\]/); // extract 'key' from [{key}]
					if (match) {
						const linkKey = match[1];
						const link = links.find(l => l.key === linkKey);
						if (link) {
							return (
								<span key={idx}>
									<IconButton
										size="small"
										component="a"
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										color="secondary"
										aria-label={`Open ${link.text} in new tab`}
									>
										<OpenInNewIcon fontSize="inherit" />
									</IconButton>
								</span>
							);
						}
					}
					return <span key={idx}>{part}</span>;
				})}
			</>
		);
	};

  return (
    <TableRow
      key={`ftr-questionnaire-row-${question_id}`}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      <StyledTableCell component="th" scope="row">
        {index + 1}
      </StyledTableCell>
      <StyledTableCell component="th" scope="row">
				{renderQuestionWithLinks(question, links)}
				{template !== "N/A" && (
          <IconButton
            onClick={handleDownloadClick}
            disabled={disabledIcon}
            type="button"
            color="secondary"
            aria-label={`Download template for question ${index + 1}`}
          >
            <CloudDownloadOutlinedIcon />
          </IconButton>
        )}
      </StyledTableCell>
      <StyledTableCell component="th" scope="row" align="center">
        <Switch
          color="success"
          checked={answer === "yes"}
          onChange={handleSwitchToggle}
          inputProps={{ "aria-label": `Toggle answer for question ${index + 1}` }}
        />
      </StyledTableCell>
			<StyledTableCell />
    </TableRow>
  );
};

export default FTRQuestionnaireRow;
